import { map } from "lodash";
import create from "zustand";
import { persist } from "zustand/middleware";
import {
  CURRENT_CALENDAR_MONTH_DISPLAY,
  PREVIOUS_CALENDAR_MONTH_DISPLAY
} from "./dates";

export enum DataPeriodKey {
  lifetime = "lifetime",
  currentMonth = "currentMonth",
  previousMonth = "previousMonth",
  oneMonth = "oneMonth",
  twoWeeks = "twoWeeks",
  oneWeek = "oneWeek"
}

interface DataPeriod {
  key: DataPeriodKey;
  title: string;
  days: number;
}

export const dataPeriods = {
  [DataPeriodKey.lifetime]: {
    key: DataPeriodKey.lifetime,
    title: "Lifetime",
    days: 0 // this is ignored
  },
  [DataPeriodKey.currentMonth]: {
    key: DataPeriodKey.currentMonth,
    title: "Current month",
    days: 0 // this is ignored
  },
  [DataPeriodKey.previousMonth]: {
    key: DataPeriodKey.previousMonth,
    title: "Previous month",
    days: 0 // this is ignored
  },
  [DataPeriodKey.oneMonth]: {
    key: DataPeriodKey.oneMonth,
    title: "Last 30D",
    days: 30
  },
  [DataPeriodKey.twoWeeks]: {
    key: DataPeriodKey.twoWeeks,
    title: "Last 14D",
    days: 14
  },
  [DataPeriodKey.oneWeek]: {
    key: DataPeriodKey.oneWeek,
    title: "Last 7D",
    days: 7
  }
};

type DataPeriodState = {
  dataPeriod: DataPeriod;
  getDataPeriodOptions: () => Array<{ key: string; title: string }>;
  setDataPeriod: (newDataPeriodKey: DataPeriodKey) => void;
  rotateDataPeriod: () => void; // This just rotates in the array of possible data periods for now
};

const useDataPeriod = create<DataPeriodState>(
  persist(
    (set) => ({
      dataPeriod: dataPeriods[DataPeriodKey.oneMonth],
      getDataPeriodOptions: () =>
        map(dataPeriods, ({ title }, key) => {
          switch (key) {
            case DataPeriodKey.currentMonth:
              return { key, title: CURRENT_CALENDAR_MONTH_DISPLAY };
            case DataPeriodKey.previousMonth:
              return { key, title: PREVIOUS_CALENDAR_MONTH_DISPLAY };
            default:
              return { key, title };
          }
        }),
      setDataPeriod: (newDataPeriodKey) =>
        set({ dataPeriod: dataPeriods[newDataPeriodKey] }),
      rotateDataPeriod: () =>
        set((state) => {
          let newDataPeriod = dataPeriods[DataPeriodKey.oneMonth];

          if (state.dataPeriod.key === DataPeriodKey.oneMonth) {
            newDataPeriod = dataPeriods[DataPeriodKey.twoWeeks];
          } else if (state.dataPeriod.key === DataPeriodKey.twoWeeks) {
            newDataPeriod = dataPeriods[DataPeriodKey.oneWeek];
          } else if (state.dataPeriod.key === DataPeriodKey.oneWeek) {
            newDataPeriod = dataPeriods[DataPeriodKey.currentMonth];
          } else if (state.dataPeriod.key === DataPeriodKey.currentMonth) {
            newDataPeriod = dataPeriods[DataPeriodKey.previousMonth];
          }

          return { dataPeriod: newDataPeriod };
        })
    }),
    {
      name: "data_period",
      version: 5
    }
  )
);

export default useDataPeriod;
